class EventBus {
  constructor() {
    this.topics = {};
  }

  subscribe(topic, listener) {
    if (!this.topics[topic]) {
      this.topics[topic] = [];
    }
    this.topics[topic].push(listener);
    return () => this.unsubscribe(topic, listener);
  }

  unsubscribe(topic, listener) {
    if (!this.topics[topic]) return;
    this.topics[topic] = this.topics[topic].filter(l => l !== listener);
  }

  publish(topic, data) {
    if (!this.topics[topic]) return;
    this.topics[topic].forEach(listener => listener(data));
  }
}

// Create a global event bus instance
window.eventBus = new EventBus();

const currencyFormatter = new Intl.NumberFormat('ko-KR', {
  style: 'currency',
  currency: 'KRW',
  maximumFractionDigits: 0,
});

function getTimeParts(delta) {
  const days = Math.floor(delta / 86400)
  delta -= days * 86400

  const hours = Math.floor(delta / 3600) % 24
  delta -= hours * 3600

  const minutes = Math.floor(delta / 60) % 60
  delta -= minutes * 60

  const seconds = Math.floor(delta % 60)  // in theory the modulus is not required

  return [days, hours, minutes, seconds]
}

function isNumber(n) {
  return typeof n === 'number' && isFinite(n)
}

function wait(duration) {
  return new Promise(resolve => {
    setTimeout(resolve, duration)
  })
}

function waitUntil(conditionFunction) {
  const poll = resolve => {
    if (conditionFunction()) resolve()
    else setTimeout(_ => poll(resolve), 400)
  }

  return new Promise(poll)
}

function isMobileViewport() {
  const screenWidth = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth
  return screenWidth <= 1023
}

function debounce(fn, wait) {
  let t
  return (...args) => {
    clearTimeout(t)
    t = setTimeout(() => fn.apply(this, args), wait)
  }
}

function partition(collection, predicate) {
  return collection.reduce((r, value) => (r[predicate(value) ? 0 : 1].push(value), r), [[], []])
}

function getCrossSellProducts() {
  return [...document.querySelectorAll('block-cross-sell')]
    .map(block => block.getAddToCartData())
    .filter(block => block.isAdded)
    .map(block => ({ quantity: block.quantity, id: block.crossSellVariantId }))
}

async function requestCart(payload = {}, url = '/cart/add.js') {
  try {
    const state = await fetch(`${url}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': `application/json`, 'Cache-Control': 'no-cache' },
      credentials: 'same-origin',
      body: JSON.stringify({ ...payload })
    });
    return await state.json();
  } catch (e) {
    return null;
  }
}

const api = {
  abortController: new AbortController(),
  abort() {
    this.abortController.abort()
  },
  async request(url, options = {}) {
    const response = await fetch(url, { ...options, signal: this.abortController.signal })
    return await response.json()
  },
  async get(url) {
    return this.request(url)
  },
  async post(url, data) {
    return this.request(url, { method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' }, body: JSON.stringify(data), })
  },
  async put(url, data) {
    return this.request(url, { method: 'PUT', body: JSON.stringify(data), headers: { 'Content-Type': 'application/json', 'Accept': `application/json` }, })
  },
  async delete(url) {
    return this.request(url, { method: 'DELETE' })
  },
}

// generate shopify ajax cart api but also using above api helper
const cartApi = {
  async executeRequest(apiCall, data) {
    try {
      const response = await apiCall(data);
      return { data: response, ok: true };
    } catch (error) {
      return { data: null, ok: false, error: error.message };
    }
  },

  async add(items, sections) {
    return this.executeRequest(
      (data) => api.post('/cart/add.js', data),
      { items, ...(sections && { sections }) }
    );
  },

  /*
   * @param {object} payload - object contains key-value pair, value is quantity and key is line item key
   */
  async update(payload, sections) {
    return this.executeRequest(
      (data) => api.post('/cart/update.js', data),
      { ...payload, ...(sections && { sections }) }
    );
  },

  async change(payload, sections) {
    return this.executeRequest(
      (data) => api.post('/cart/change.js', data),
      { ...payload, ...(sections && { sections }) }
    );
  },

  async clear() {
    return this.executeRequest(
      (data) => api.post('/cart/clear.js', data),
      {}
    );
  },

  async get() {
    return this.executeRequest(
      (data) => api.get(`/cart.js?t=${Date.now()}`, data),
      {}
    );
  },
};

function updateCrossSellVariantTitle() {
  const crossSellTitle = document.querySelector('p:has(+block-cross-sell)')
  if (!crossSellTitle) return

  const crossSellComponents = [...document.querySelectorAll('block-cross-sell')]
  const activeCrossSellComponents = crossSellComponents.filter(item => item.state.currentCrossSellProductVariant)
  crossSellTitle.classList[activeCrossSellComponents.length ? 'remove' : 'add']('hidden')
}

class EventStore {
  constructor() {
    this.eventTarget = new EventTarget();
  }

  // Dispatch an event to the event store
  dispatchEvent(eventName, detail = {}) {
    const event = new CustomEvent(eventName, { detail });
    this.eventTarget.dispatchEvent(event);
  }

  // Listen for an event from the event store
  addEventListener(eventName, callback) {
    this.eventTarget.addEventListener(eventName, callback);
  }

  // Remove an event listener from the event store
  removeEventListener(eventName, callback) {
    this.eventTarget.removeEventListener(eventName, callback);
  }
}

class StateStore extends EventStore {
  constructor(initialState = {}) {
    super()
    this.state = initialState
  }

  getState = () => this.state

  setState(newState) {
    const prevState = this.state
    this.state = { ...this.state, ...newState }
    this.dispatchEvent('stateChange', { state: this.state, prevState })
  }

  subscribe(callback) {
    this.addEventListener('stateChange', ({ detail: { state, prevState } }) => {
      callback({ detail: state }, prevState)
    })
  }

  unsubscribe(callback) {
    this.removeEventListener('stateChange', callback)
  }
}

const ProductPageStore = new StateStore();

const GlobalStore = new StateStore();

function overrideRequest() {
  const updatePaths = ['cart.js'];

  const coreFetch = window.fetch;
  
  window.fetch = async (url, data) => {
    if (typeof (url) !== 'string') {
      return coreFetch(url, data)
    }
    if (updatePaths.find(updateUrl => url.includes(updateUrl))) {
      const response = await coreFetch(url, data);
      const responseClone = response.clone();
      responseClone.json().then((currentCart) => {
        GlobalStore && GlobalStore.setState({ currentCart });
      });
      return response;
    }

    return coreFetch(url, data)
  }

  const rawOpen = XMLHttpRequest.prototype.open;

  XMLHttpRequest.prototype.open = function() {
    const url = this._url;
    if (!this._hooked && updatePaths.find(updateUrl => String(url).includes(updateUrl))) {
      this._hooked = true;
      this.onreadystatechange = function() {
        if (this.readyState == 4) {
          const currentCart = JSON.parse(this.responseText);
          GlobalStore && GlobalStore.setState({ currentCart });
        }
      };
    }
    rawOpen.apply(this, arguments);
  }
}

class CacheStore {
  constructor() {
    this.cache = new Map();
  }

  // Get data from the cache
  get(key) {
    return this.cache.get(key);
  }

  // Set data to the cache
  set(key, value) {
    this.cache.set(key, value);
  }

  // Check if the cache has the key
  has(key) {
    return this.cache.has(key);
  }
}

const Cache = new CacheStore();

function fetchProductPage(handle) {
  if (Cache.has(handle)) {
    return Promise.resolve(Cache.get(handle));
  }

  return fetch(`/products/${handle}`)
    .then(response => response.text())
    .then(html => {
      Cache.set(handle, html);
      return html;
    });
}

function initTabs(section) {
  const tabs = section.querySelectorAll('.tab_contents');
  const buttons = section.querySelectorAll('.tab_link');
  const products = section.querySelectorAll('.product-container');

  function onClick(evt) {
    const button = evt.target;
    const tabName = button.getAttribute('data-tab');
    const targetTab = section.querySelector(`.tab_contents[data-tab="${tabName}"]`);

    if (!targetTab) return;

    updateTabs(tabs, tabName);
    updateButtons(buttons, tabName);
  }

  function updateTabs(tabs, activeTabName) {
    tabs.forEach(tab => {
      const tabName = tab.getAttribute('data-tab');
      if (tabName === activeTabName) {
        tab.classList.add('grid');
        tab.classList.remove('hidden');
      } else {
        tab.classList.remove('grid');
        tab.classList.add('hidden');
      }
    });
  }

  function updateButtons(buttons, activeTabName) {
    buttons.forEach(button => {
      const tabName = button.getAttribute('data-tab');
            if (tabName === activeTabName) {
        button.classList.add("text-gray-900");
        button.classList.add("font-semibold");
        button.classList.add("border-gray-800");
        button.classList.remove("text-gray-600");
        button.classList.remove("hover:text-gray-900");
        button.classList.remove("hover:border-gray-400");
        button.classList.remove("border-transparent");
      } else {
        button.classList.remove("text-gray-900");
        button.classList.remove("font-semibold");
        button.classList.remove("border-gray-800");
        button.classList.add("text-gray-600");
        button.classList.add("hover:text-gray-900");
        button.classList.add("hover:border-gray-400");
        button.classList.add("border-transparent");
      }
    });
  }

  function moveProductsToTabs(products) {
    products.forEach(product => {
      const tabName = product.getAttribute('data-tab');
      const targetTab = section.querySelector(`.tab_contents[data-tab="${tabName}"]`);
      if (targetTab) {
        targetTab.appendChild(product);
      }
    });
  }

  return {
    init: function () {
      moveProductsToTabs(products);
      buttons.forEach(button => {
        button.addEventListener('click', onClick);
      });
    },
  };
}

const html = String.raw

class ProductSelectorService {
  constructor(eventBus) {
    this.productSelectorComponents = []
    this.eventBus = eventBus
    this.renderCount = 0

    this.initService()
  }

  initService() {
    this.eventBus.subscribe(
      'block-product-selector:rendered',
      this.handleProductSelectorRendered.bind(this)
    )
  }

  handleProductSelectorRendered(data) {
    this.renderCount++
    this.productSelectorComponents.push({
      component: data.component,
      price: data.price,
      handle: data.handle,
    })

    if (this.isAllComponentsRendered()) {
      if (this.isRenderSpecificProduct() && this.isSpecificProductValid()) this.selectSpecificProductComponent()
    }
  }

  isRenderSpecificProduct() {
    const urlParams = new URLSearchParams(window.location.search)
    return urlParams.has('view_product')
  }

  isSpecificProductValid() {
    const urlParams = new URLSearchParams(window.location.search)
    const handle = urlParams.get('view_product')
    return this.productSelectorComponents.some((component) => component.handle === handle)
  }

  isAllComponentsRendered() {
    return this.renderCount === document.querySelectorAll('block-product-selector').length
  }

  selectSpecificProductComponent() {
    const urlParams = new URLSearchParams(window.location.search)
    const handle = urlParams.get('view_product')
    const specifiedProductComponent = this.productSelectorComponents.find(
      (component) => component.handle === handle
    )
    setTimeout(() => specifiedProductComponent.component.click(), 0)
  }

  selectLowestPriceComponent() {
    const component = this.productSelectorComponents.reduce(
      (accumulator, currentValue) => {
        if (currentValue.price < accumulator.price) {
          return currentValue
        }

        return accumulator
      },
      { component: null, price: Infinity }
    )

    setTimeout(() => component.component.click(), 0)
  }
}

const productSelectorService = new ProductSelectorService(window.eventBus)

class CartService {
  constructor(cartApi, productPageStore) {
    this.cartApi = cartApi;
    this.productPageStore = productPageStore
  }

  async get() {
    return this.cartApi.get();
  }

  async add(items, sections = undefined) {
    return this.cartApi.add(items, sections);
  }

  async update(payload, sections = undefined) {
    return this.cartApi.update(payload, sections);
  }

  async change(payload, sections = undefined) {
    return this.cartApi.change(payload, sections);
  }

  async remove(key) {
    return this.cartApi.change({ id: key, quantity: 0 });
  }

  async addSingle(id, sections = undefined, properties = {}) {
    return this.cartApi.add([{ id, quantity: 1, properties }], sections);
  }
}

const cartService = new CartService(cartApi, ProductPageStore);

class CustomElement extends HTMLElement {
  props = null

  static get observedAttributes() {
    return ['disabled']
  }

  _value = null
  get value() {
    return this._value
  }

  set value(newVal) {
    if (newVal === this._value) return
    this._value = newVal

    this.onValueChange(newVal)
  }

  _loading = false
  get loading() {
    return this._loading
  }

  set loading(newVal) {
    if (newVal === this._loading) return
    this._loading = newVal
    this.onLoad(newVal)
  }

  _disabled = false
  get disabled() {
    return this._disabled
  }

  set disabled(isDisabled) {
    if (isDisabled === this._disabled) return
    this._disabled = isDisabled
    this.onDisabledChange(isDisabled)
  }

  _readOnly = false
  get readOnly() {
    return this._readOnly
  }

  set readOnly(isReadOnly) {
    if (isReadOnly === this._readOnly) return
    this._readOnly = isReadOnly
    this.onReadOnlyChanged(isReadOnly)
  }


  constructor() {
    super()
    this.$el = $(this)
    this.setup()
  }

  connectedCallback() {
    this.extractProps()
    this.init()

    this.beforeMount()
    this.render()

    setTimeout(() => {
      $(this).ready(this.mounted.bind(this))
    }, 0)
  }

  disconnectedCallback() {
    this.mutationObserver?.disconnect()

    this.onDestroy()
  }


  setup() {
    this.mutationObserver = new MutationObserver(this.onMutation.bind(this))
  }

  init() {
    this.$el.addClass(`${this.tagName.toLowerCase()}`)
  }

  extractProps() {
    const isObject = typeof this.props === 'object' && this.props !== null

    if (!isObject) return

    this.props = Object.entries(this.props).reduce((r, [propName, defaultValue]) => {
      let propVal


      const rawVal = this.getAttribute(`:${propName.toLowerCase()}`)

      try {
        propVal = JSON.parse(rawVal)
      } catch {
        propVal = rawVal
      }

      r[propName] = propVal || defaultValue

      return r
    }, {})

    this.value = this.getAttribute(`:value`)

    this.readOnly = this.hasAttribute('readOnly') || this.getAttribute('readOnly') === 'readOnly'
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'disabled') {
      this.disabled = newValue === 'disabled'
    }
  }

  beforeMount() {
  }

  template() {

  }

  render() {
    if (this.template()) {
      this.innerHTML = this.template()
    }
  }

  mounted() {
  }

  onMutation() {
  }

  onDestroy() {
  }

  onValueChange() {
  }

  onLoad(isLoading) {
    this.$el[!!isLoading ? 'addClass' : 'removeClass']('is-loading')

    const firstLoadingOverlays = this.$el.find('loading-overlay').first().get(0)

    if (!!firstLoadingOverlays) {
      if (isLoading) firstLoadingOverlays.show()
      else firstLoadingOverlays.hide()
    }
  }

  onDisabledChange(isDisabled) {
    if (!!isDisabled) {
      this.$el.attr('disabled', 'disabled')
    } else {
      this.$el.removeAttr('disabled')
    }
  }

  onReadOnlyChanged(isReadOnly) {
    this.$el[!!isReadOnly ? 'attr' : 'removeAttr']('readOnly', 'readOnly')
  }
}

class CustomButton extends CustomElement {
  constructor() {
    super()

    this.addEventListener('click', (event) => {
      event.preventDefault()

      if (this.disabled || this.readOnly) {
        event.stopImmediatePropagation()
        return
      }

      this.onClick(event)
    })

  }

  onClick(e) {

  }
}

class ResponsiveComponent extends CustomElement {
  props = {}

  _isMobile = null
  get isMobile() {
    return this._isMobile
  }

  set isMobile(isMobile) {
    if (isMobile === this._isMobile) return
    this._isMobile = isMobile

    this.onLayoutModeChange(isMobile)
  }

  constructor() {
    super()
  }

  disconnectedCallback() {
    super.disconnectedCallback()

    this.resizeObserver?.disconnect()
  }


  setup() {
    super.setup()

    this.resizeObserver = new ResizeObserver(debounce(this.onScreenResize.bind(this), 500))

    this.templates = [...this.querySelectorAll('template')].reduce((r, node) => {
      r[node.getAttribute('name')] = node.content
      return r
    }, {})
  }

  beforeMount() {
    super.beforeMount()

    this.resizeObserver.observe(document.body)
  }


  render() {
    if (this.isMobile === null) return

    const tpl = this.templates[this.isMobile ? 'mobile' : 'desktop']

    this.$el.html(tpl.cloneNode(true))
  }

  onScreenResize(entries) {
    const [entry] = entries

    const screenWidth = entry.contentRect.width

    this.isMobile = screenWidth <= 769
  }

  onLayoutModeChange(isMobile) {
    if (isMobile) {
      this.$el.addClass('mobile').removeClass('desktop')
    } else {
      this.$el.removeClass('mobile').addClass('desktop')
    }

    this.render()
  }
}

customElements.define('responsive-component', ResponsiveComponent)

class LoadingOverlay extends CustomElement {
  props = {
    type: 'spinner',
    center: false,
  }

  beforeMount() {
    super.beforeMount()

    const { center } = this.props

    if (center) {
      this.$el.addClass('is-center')
    }

    this.hide()
  }

  render() {
    const { type } = this.props

    const allTypes = {
      'spinner': this.renderSpinner(),
    }

    const template = allTypes[type || 'spinner']

    this.$el.html(template)
  }

  renderSpinner() {
    return `
      <svg
        aria-hidden="true" focusable="false" role="presentation" class="spinner" viewBox="0 0 66 66"
        xmlns="http://www.w3.org/2000/svg"
      >
        <circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle>
      </svg>
    `
  }

  show() {
    this.$el.removeClass('hidden')
  }

  hide() {
    this.$el.addClass('hidden')
  }
}

customElements.define('loading-overlay', LoadingOverlay)

class ResourceCoordinatorBuilder {
  #cached = new Set()
  #resources = new Map()
  #loadingPromises = new Map()

  registerVendor(vendorId, { urls = [], version = '1', postFetch = null }) {
    this.#resources.set(vendorId, { urls, version, postFetch })
  }

  unregisterVendor(vendorId) {
    this.#resources.delete(vendorId)
    this.#cached.delete(vendorId)
  }

  async requestVendor(vendorId) {
    if (!vendorId) throw new Error('Please enter resource vendorId')

    const cacheKey = `${vendorId}_${this.#resources.get(vendorId)?.version || ''}`
    if (this.#cached.has(cacheKey)) return

    if (this.#loadingPromises.has(cacheKey)) {
      return this.#loadingPromises.get(cacheKey)
    }

    const loadPromise = this.#loadVendorResources(vendorId, cacheKey)
    this.#loadingPromises.set(cacheKey, loadPromise)

    try {
      await loadPromise
    } finally {
      this.#loadingPromises.delete(cacheKey)
    }
  }

  async #loadVendorResources(vendorId, cacheKey) {
    const resource = this.#resources.get(vendorId)
    if (!resource) throw new Error(`Cannot find resource for vendor: ${vendorId}`)

    const { urls, postFetch } = resource
    const [stylesheets, scripts] = this.#partition(urls, url => url.endsWith('.css'))

    try {
      await Promise.all([
        this.#loadStylesheets(stylesheets),
        this.#loadScripts(scripts),
      ])

      if (postFetch) await postFetch()

      this.#cached.add(cacheKey)
    } catch (error) {
      console.error(`Failed to load resources for vendor ${vendorId}:`, error)
      throw error
    }
  }

  #partition(collection, predicate) {
    return collection.reduce((result, item) => {
      result[predicate(item) ? 0 : 1].push(item)
      return result
    }, [[], []])
  }

  async #loadStylesheets(urls) {
    return Promise.all(urls.map(url => this.#loadStylesheet(url)))
  }

  #loadStylesheet(href) {
    return new Promise((resolve, reject) => {
      const url = new URL(href, 'https://cdn.shopify.com/')

      if (!url.searchParams.has('v')) {
        url.searchParams.set('v', Date.now().toString())
      }

      const link = document.createElement('link')
      link.rel = 'stylesheet'
      link.type = 'text/css'
      link.href = url.toString()
      link.media = 'print'

      link.onload = () => {
        link.media = 'all'
        resolve(link)
      }
      link.onerror = reject

      const vendorCustomStyles = document.getElementById('vendor-custom-styles')
      if (vendorCustomStyles) {
        vendorCustomStyles.parentNode.insertBefore(link, vendorCustomStyles)
      } else {
        document.head.appendChild(link)
      }
    })
  }

  async #loadScripts(urls) {
    for (const url of urls) {
      await this.#loadScript(url)
    }
  }

  #loadScript(url) {
    return new Promise((resolve, reject) => {
      const script = document.createElement('script')
      script.src = url
      script.defer = true
      script.onload = resolve
      script.onerror = reject
      document.head.appendChild(script)
    })
  }

  clearCache() {
    this.#cached.clear()
  }
}

window.ResourceCoordinator = new ResourceCoordinatorBuilder()

ResourceCoordinator.registerVendor(
  'Toastr',
  {
    urls: [
      'https://cdnjs.cloudflare.com/ajax/libs/toastr.js/latest/toastr.min.css',
      'https://cdnjs.cloudflare.com/ajax/libs/toastr.js/latest/toastr.min.js',
    ],
    postFetch: () => {
      toastr.options.closeButton = true
      toastr.options.timeOut = 10 * 1000
      toastr.options.preventOpenDuplicates = true
    },
  })

ResourceCoordinator.registerVendor('Mediabox', {
  urls: [
    'https://cdn.jsdelivr.net/npm/mediabox@1.1.3/dist/mediabox.min.css',
    'https://cdn.jsdelivr.net/npm/mediabox@1.1.3/dist/mediabox.min.js',
  ],
  version: '1.1.3',
})

ResourceCoordinator.registerVendor('Beefup', {
  urls: ['https://cdn.jsdelivr.net/npm/beefup@1.1.7/dist/js/jquery.beefup.min.js'],
  version: '1.1.7',
})

ResourceCoordinator.registerVendor('JqueryDebounce', {
  urls: ['https://cdn.jsdelivr.net/npm/jquery-throttle-debounce@1.0.0/jquery.ba-throttle-debounce.min.js'],
  version: '1.0.0',
})

ResourceCoordinator.registerVendor('JqueryModal', {
  urls: [
    'https://cdnjs.cloudflare.com/ajax/libs/jquery-modal/0.9.1/jquery.modal.min.css',
    'https://cdnjs.cloudflare.com/ajax/libs/jquery-modal/0.9.1/jquery.modal.min.js',
  ],
  version: '0.9.1',
})

ResourceCoordinator.registerVendor('Select2', {
  urls: [
    'https://cdn.jsdelivr.net/npm/select2@4.1.0-rc.0/dist/css/select2.min.css',
    'https://cdn.jsdelivr.net/npm/select2@4.1.0-rc.0/dist/js/select2.min.js',
  ],
  version: '4.1.0-rc.0',
})

ResourceCoordinator.registerVendor('Swiper', {
  urls: [
    'https://cdn.jsdelivr.net/npm/swiper/swiper-bundle.min.css',
    'https://cdn.jsdelivr.net/npm/swiper/swiper-bundle.min.js',
  ],
  version: 'latest', // You might want to specify an exact version here
})

ResourceCoordinator.registerVendor('Splide', {
  urls: [
    'https://cdn.jsdelivr.net/npm/@splidejs/splide@4.0.1/dist/js/splide.min.js',
    'https://cdn.jsdelivr.net/npm/@splidejs/splide@4.0.1/dist/css/splide.min.css',
  ],
  version: '4.0.1',
})

ResourceCoordinator.registerVendor('Tippy', {
  urls: [
    'https://cdn.jsdelivr.net/npm/tippy.js@6.3.7/dist/tippy.min.css',
    'https://cdn.jsdelivr.net/npm/tippy.js@6.3.7/dist/tippy.umd.min.js',
  ],
  version: '6.3.7',
})

ResourceCoordinator.registerVendor('TippyDependency', {
  urls: ['https://cdn.jsdelivr.net/npm/@popperjs/core@2.11.8/dist/umd/popper.min.js'],
  version: '2', // You might want to specify an exact version here
})

ResourceCoordinator.registerVendor('Pikaday', {
  urls: [
    'https://cdn.jsdelivr.net/npm/pikaday@1.8.0/css/pikaday.min.css',
    'https://cdn.jsdelivr.net/npm/pikaday@1.8.0/pikaday.min.js',
  ],
  version: '1.8.0',
})
